import React, { useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { Input } from "@design-system/Input/Input";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useCreateWorkspaceEmailAlias } from "@api/workspaceEmailAlias.api";
import { useEntityEmailAddresses } from "@api/entity.api";
import { didRequestReachServer } from "@api/apiError";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { WorkspaceEmailAliasKind } from "@common/constants/domains/receipts/constants";
import { getWorkspaceEmailAliasDomain } from "@common/helpers/workspace-email-alias";
import {
    CounterpartyCombobox,
    CounterpartyComboboxValue,
} from "@components/transactions/TransactionSplitV2/CounterpartyCombobox";
import { EntitySelect } from "@components/entity/EntitySelect";
import { useEntities } from "@hooks/useEntities";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import styles from "./AddEmailAliasModal.module.scss";

const formSchema = z.object({
    customName: z
        .string()
        .trim()
        .toLowerCase()
        .refine(
            (value) => value.length === 0 || /^[a-z0-9]+$/.test(value),
            "Use lowercase letters and digits only",
        ),
    entityId: z.number().int().positive(),
    counterpartyId: z.string().uuid().nullable(),
});

interface FormValues {
    customName: string;
    entityId: number | null;
    counterpartyId: string | null;
}

interface AddEntityCounterpartyEmailAliasModalProps {
    show: boolean;
    onClose: () => void;
    kind: Exclude<WorkspaceEmailAliasKind, "receipts">;
}

export const AddEntityCounterpartyEmailAliasModal: React.FC<
    AddEntityCounterpartyEmailAliasModalProps
> = ({ show, onClose, kind }) => {
    const createEmailAlias = useCreateWorkspaceEmailAlias();
    const { toast, error } = useToaster();
    const entities = useEntities({ onlyBusiness: true });
    const [selectedCounterparty, setSelectedCounterparty] = useState<
        CounterpartyComboboxValue | undefined
    >();
    const domain = getWorkspaceEmailAliasDomain(kind);

    const formik = useFormik<FormValues>({
        initialValues: {
            customName: "",
            entityId: null,
            counterpartyId: null,
        },
        validate: validateFormikWithZodSchema(formSchema),
        validateOnMount: true,
        enableReinitialize: true,
        onSubmit: submitHelper({
            handler: async ({ customName, entityId, counterpartyId }) => {
                const entityUuid =
                    entityId == null
                        ? undefined
                        : entities.find((entity) => entity.id === entityId)
                              ?.uuid;
                if (entityId == null || !entityUuid) {
                    return;
                }
                await createEmailAlias.mutateAsync({
                    customName: customName.trim().toLowerCase(),
                    kind,
                    entityUuid,
                    counterpartyId,
                });
                toast("Email alias created");
                handleClose();
            },
            failureHandler: async (requestError) => {
                if (!didRequestReachServer(requestError)) {
                    error("Failed to create email alias");
                }
            },
        }),
    });

    const emailAddresses = useEntityEmailAddresses(
        formik.values.entityId ?? undefined,
        { enabled: formik.values.entityId != null },
    );
    const entitySlug = emailAddresses.data?.slug;

    const handleClose = () => {
        formik.resetForm();
        setSelectedCounterparty(undefined);
        onClose();
    };

    return (
        <PlainModal show={show} onHide={handleClose}>
            <StandardModalHeader>Add Email Alias</StandardModalHeader>
            <FormikProvider value={formik}>
                <Form>
                    <VStack gap={16}>
                        <FormikField
                            name="entityId"
                            label="Entity"
                            control="button"
                        >
                            <EntitySelect
                                entities={entities}
                                selectedEntity={entities.find(
                                    (entity) =>
                                        entity.id === formik.values.entityId,
                                )}
                                onChange={(entity) => {
                                    void formik.setFieldValue(
                                        "entityId",
                                        entity?.id ?? null,
                                    );
                                }}
                                placeholder="Select entity"
                                triggerButtonProps={{
                                    variant: "primary",
                                    block: true,
                                }}
                            />
                        </FormikField>

                        <FormikField<string> name="customName" label="Name">
                            {({ field, invalid }) => (
                                <div className={styles.fieldWrapper}>
                                    <Input
                                        {...field}
                                        placeholder="acme"
                                        type="text"
                                        size="md"
                                        className={styles.field}
                                        invalid={invalid}
                                        onChange={(event) => {
                                            void formik.setFieldValue(
                                                "customName",
                                                event.target.value
                                                    .toLowerCase()
                                                    .replace(/[^a-z0-9]/g, ""),
                                            );
                                        }}
                                    />
                                    <span className={styles.addon}>
                                        {entitySlug
                                            ? `.${entitySlug}@${domain}`
                                            : `@${domain}`}
                                    </span>
                                </div>
                            )}
                        </FormikField>

                        <FormikField
                            name="counterpartyId"
                            label="Counterparty"
                            control="button"
                            optional
                        >
                            <CounterpartyCombobox
                                selectedCounterparty={selectedCounterparty}
                                onChange={(counterparty) => {
                                    setSelectedCounterparty(counterparty);
                                    void formik.setFieldValue(
                                        "counterpartyId",
                                        counterparty?.id ?? null,
                                    );
                                }}
                                placeholder="Select counterparty"
                                searchPlaceholder="Search counterparties..."
                                createButtonText="Create counterparty"
                                emptyStateText="No matching counterparties"
                            />
                        </FormikField>

                        <HStack as="footer" justify="end" gap={12}>
                            <Button
                                variant="secondary"
                                size="xl"
                                type="button"
                                onClick={handleClose}
                                disabled={formik.isSubmitting}
                            >
                                Cancel
                            </Button>
                            <ButtonWithLoader
                                variant="primary"
                                size="xl"
                                disabled={
                                    !formik.isValid ||
                                    !formik.values.customName ||
                                    !entitySlug ||
                                    emailAddresses.isLoading
                                }
                                loading={formik.isSubmitting}
                                type="submit"
                            >
                                Save
                            </ButtonWithLoader>
                        </HStack>
                    </VStack>
                </Form>
            </FormikProvider>
        </PlainModal>
    );
};
